import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Play, Target, TrendingUp, AlertCircle, Award } from 'lucide-react';
import { toast } from 'sonner';
import { OptimizationService } from '@/services/OptimizationService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode } from '@/types/trading-analysis-types';

interface SequenceResult {
  sequenceLength: number;
  accuracy: number;
  totalPredictions: number;
  skipRate: number;
  stabilityScore: number;
  processingTime: number;
  isOptimal?: boolean;
}

interface SensitivityAnalysisResult {
  results: SequenceResult[];
  optimalLength: number;
  sweetSpotRange: [number, number];
  stabilityScore: number;
}

interface SequenceSensitivityAnalyzerProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
  winThreshold: number;
}

export const SequenceSensitivityAnalyzer: React.FC<SequenceSensitivityAnalyzerProps> = ({
  selectedFile,
  selectedAnalysisMode,
  winThreshold
}) => {
  const [sequenceRange, setSequenceRange] = useState<[number, number]>([3, 15]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<SensitivityAnalysisResult | null>(null);

  const handleRangeChange = (values: number[]) => {
    setSequenceRange([values[0], values[1]]);
  };

  const handleAnalyze = async () => {
    if (!selectedFile?.id) {
      toast.error('Please select a file first');
      return;
    }

    if (sequenceRange[1] - sequenceRange[0] < 2) {
      toast.error('Range must include at least 3 different lengths');
      return;
    }

    setIsAnalyzing(true);
    setProgress(0);
    setResults(null);

    try {
      const result = await OptimizationService.runSequenceSensitivityAnalysis({
        fileId: selectedFile.id,
        sequenceLengthRange: sequenceRange,
        winThreshold,
        analysisMode: selectedAnalysisMode
      }, (progressUpdate) => {
        setProgress((progressUpdate.completed / progressUpdate.total) * 100);
      });

      if (result.success && result.data) {
        setResults(result.data);
        toast.success(`Analysis complete! Optimal length: ${result.data.optimalLength}`);
      } else {
        toast.error(result.error || 'Analysis failed');
      }
    } catch (error) {
      console.error('Sensitivity analysis error:', error);
      toast.error('Failed to run sensitivity analysis');
    } finally {
      setIsAnalyzing(false);
      setProgress(0);
    }
  };

  const renderConfiguration = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Sequence Length Configuration
        </CardTitle>
        <CardDescription>
          Configure the range of sequence lengths to analyze for optimal performance
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>
              Sequence Length Range: {sequenceRange[0]} - {sequenceRange[1]}
            </Label>
            <Slider
              value={sequenceRange}
              onValueChange={handleRangeChange}
              min={3}
              max={20}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Will test {sequenceRange[1] - sequenceRange[0] + 1} different sequence lengths
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm font-medium">Current Threshold</p>
              <p className="text-2xl font-bold">{(winThreshold * 100).toFixed(0)}%</p>
            </div>
            <div>
              <p className="text-sm font-medium">Analysis Mode</p>
              <p className="text-lg font-semibold capitalize">{selectedAnalysisMode}</p>
            </div>
          </div>
        </div>

        <Button 
          onClick={handleAnalyze} 
          disabled={isAnalyzing || !selectedFile}
          className="w-full"
        >
          <Play className="h-4 w-4 mr-2" />
          {isAnalyzing ? 'Analyzing...' : 'Start Sensitivity Analysis'}
        </Button>

        {isAnalyzing && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Testing sequence lengths...</span>
              <span>{progress.toFixed(0)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderResults = () => {
    if (!results) return null;

    const chartData = results.results.map(result => ({
      length: result.sequenceLength,
      accuracy: result.accuracy,
      predictions: result.totalPredictions,
      skipRate: result.skipRate,
      stability: result.stabilityScore
    }));

    const optimalResult = results.results.find(r => r.sequenceLength === results.optimalLength);

    return (
      <div className="space-y-6">
        {/* Optimal Length Card */}
        {optimalResult && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-primary" />
                Optimal Sequence Length
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {results.optimalLength}
                  </div>
                  <div className="text-sm text-muted-foreground">Optimal Length</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{optimalResult.accuracy.toFixed(1)}%</div>
                  <div className="text-sm text-muted-foreground">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{optimalResult.totalPredictions}</div>
                  <div className="text-sm text-muted-foreground">Predictions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{optimalResult.stabilityScore.toFixed(1)}</div>
                  <div className="text-sm text-muted-foreground">Stability</div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-background rounded-lg">
                <p className="text-sm">
                  <strong>Sweet Spot Range:</strong> {results.sweetSpotRange[0]} - {results.sweetSpotRange[1]} 
                  {' '}(lengths that perform within 5% of optimal)
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sensitivity Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Sequence Length Sensitivity</CardTitle>
            <CardDescription>
              How accuracy changes with different sequence lengths
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="length" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip 
                    formatter={(value: number, name: string) => [
                      name === 'accuracy' ? `${value.toFixed(1)}%` : 
                      name === 'stability' ? `${value.toFixed(1)}` : value,
                      name === 'accuracy' ? 'Accuracy' : 
                      name === 'stability' ? 'Stability' : 'Predictions'
                    ]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="accuracy" 
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary) / 0.2)"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="stability" 
                    stroke="hsl(var(--secondary))" 
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Performance Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Performance Breakdown</CardTitle>
            <CardDescription>
              Detailed metrics for each sequence length tested
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Length</TableHead>
                  <TableHead>Accuracy</TableHead>
                  <TableHead>Predictions</TableHead>
                  <TableHead>Skip Rate</TableHead>
                  <TableHead>Stability</TableHead>
                  <TableHead>Time (ms)</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.results
                  .sort((a, b) => b.accuracy - a.accuracy)
                  .map((result, index) => (
                  <TableRow 
                    key={result.sequenceLength} 
                    className={result.sequenceLength === results.optimalLength ? 'bg-primary/5' : ''}
                  >
                    <TableCell className="font-medium">
                      {result.sequenceLength}
                    </TableCell>
                    <TableCell>
                      <Badge variant={result.accuracy >= 60 ? 'default' : 'secondary'}>
                        {result.accuracy.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    <TableCell>{result.totalPredictions}</TableCell>
                    <TableCell>{result.skipRate.toFixed(1)}%</TableCell>
                    <TableCell>{result.stabilityScore.toFixed(1)}</TableCell>
                    <TableCell>{result.processingTime}</TableCell>
                    <TableCell>
                      {result.sequenceLength === results.optimalLength && (
                        <Badge variant="default" className="text-xs">
                          <Award className="h-3 w-3 mr-1" />
                          Optimal
                        </Badge>
                      )}
                      {results.sweetSpotRange[0] <= result.sequenceLength && 
                       result.sequenceLength <= results.sweetSpotRange[1] && 
                       result.sequenceLength !== results.optimalLength && (
                        <Badge variant="outline" className="text-xs">
                          Sweet Spot
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file to run sequence sensitivity analysis
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Sequence Length Sensitivity</h3>
          <p className="text-sm text-muted-foreground">
            Analyze how sequence length affects prediction accuracy
          </p>
        </div>
        <Badge variant="outline">File: {selectedFile.filename}</Badge>
      </div>

      {renderConfiguration()}
      {renderResults()}
    </div>
  );
};